import { networkService } from '../NetworkService';
import { databaseService } from './DatabaseService';
import { ConnectionStatus } from '@capacitor/network';

interface OfflineModeConfig {
  autoSync: boolean;
  syncInterval: number; // minutes
  maxRetryAttempts: number;
  cacheDuration: number; // hours
  enableBackgroundSync: boolean;
}

interface OfflineStatus {
  isOnline: boolean;
  isOfflineMode: boolean;
  hasPendingSync: boolean;
  lastSyncTime: Date | null;
  cacheSize: number;
  pendingOperations: number;
}

type OfflineModeListener = (status: OfflineStatus) => void;

class OfflineModeManager {
  private config: OfflineModeConfig = {
    autoSync: true,
    syncInterval: 5, // 5 minutes
    maxRetryAttempts: 3,
    cacheDuration: 24, // 24 hours
    enableBackgroundSync: true
  };

  private listeners: OfflineModeListener[] = [];
  // private syncIntervalId: NodeJS.Timeout | null = null;
  private isInitialized = false;
  private currentStatus: OfflineStatus = {
    isOnline: false,
    isOfflineMode: false,
    hasPendingSync: false,
    lastSyncTime: null,
    cacheSize: 0,
    pendingOperations: 0
  };

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      
      console.log('🔄 OfflineModeManager initializing...');

      // Initialize database
      await databaseService.initialize();
      
      // Get initial network status
      const networkStatus = networkService.getCurrentStatus();
      await this.handleNetworkStatusChange(networkStatus);

      // Listen to network changes
      networkService.addListener(this.handleNetworkStatusChange.bind(this));

      // Set up periodic sync if enabled
      // if (this.config.autoSync && this.config.syncInterval > 0) {
      //   this.startPeriodicSync();
      // }

      // Load last sync time from cache
      // const lastSync = await databaseService.getCacheValue('last_sync_time');
      // if (lastSync) {
      //   this.currentStatus.lastSyncTime = new Date(lastSync);
      // }

      this.isInitialized = true;
      console.log('✅ OfflineModeManager initialized');
      
      await this.updateStatus();
    } catch (error) {
      console.error('❌ Failed to initialize OfflineModeManager:', this.isInitialized, error);
      throw error;
    }
  }

  private async handleNetworkStatusChange(status: ConnectionStatus | null): Promise<void> {
    // const wasOnline = this.currentStatus.isOnline;
    const isNowOnline = status?.connected ?? false;

    this.currentStatus.isOnline = isNowOnline;
    this.currentStatus.isOfflineMode = !isNowOnline;

    // If we just came online, trigger sync
    // if (!wasOnline && isNowOnline && this.config.autoSync) {
    //   console.log('🔄 Network restored, starting sync...');
    //   await this.startSync();
    // }

    await this.updateStatus();
  }

  private async updateStatus(): Promise<void> {
    try {
      // // Get pending sync operations
      // const pendingQueue = await databaseService.getSyncQueue('pending');
      // this.currentStatus.pendingOperations = pendingQueue.length;
      // this.currentStatus.hasPendingSync = pendingQueue.length > 0;

      // // Get cache size
      // const stats = await databaseService.getStorageStats();
      // this.currentStatus.cacheSize = stats.usedSize;

      // Notify listeners
      this.notifyListeners();
    } catch (error) {
      console.error('Error updating offline status:', error);
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.currentStatus);
      } catch (error) {
        console.error('Error in offline mode listener:', error);
      }
    });
  }

  async manualUpdateOfflineMode(isOffline: boolean): Promise<void> {
    console.log('Manually updating offline mode. New status:', isOffline);
    this.currentStatus.isOfflineMode = isOffline;
    await this.updateStatus();
  }


  async getStorageStats(): Promise<any> {
    return await databaseService.getStorageStats();
  }

  getCurrentStatus(): OfflineStatus {
    return { ...this.currentStatus };
  }

  addListener(listener: OfflineModeListener): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  updateConfig(newConfig: Partial<OfflineModeConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart periodic sync if interval changed
    // if (newConfig.syncInterval !== undefined) {
    //   this.startPeriodicSync();
    // }
  }

  getConfig(): OfflineModeConfig {
    return { ...this.config };
  }
}

// Export singleton instance
export const offlineModeManager = new OfflineModeManager();
export default offlineModeManager;